import { Injectable } from '@angular/core';

import { ObservationValidationResult } from '../observation/validation/observation-validation-result.model';

import {
    FOCUS_CONTAINERS_TRACKING_ID,
    ObservationFormDecorateConnector,
    ValidationResult,
} from './observation-form-decorate-connector';

/** this is being used instead of a direct form validator due to odd requirements and existing behaviour */
@Injectable()
export class ObservationFormDecoratorService {
    private readonly _trackingIdInstanceMap: {
        [trackingId: number]: ObservationFormDecorateConnector;
    };

    private _result: ObservationValidationResult;

    constructor() {
        this._trackingIdInstanceMap = {};
    }

    /** instances implementing CoachingDiffDecorateConnector can call this */
    registerForTrackingId(
        trackingId: number,
        instance: ObservationFormDecorateConnector,
    ): void {
        this._trackingIdInstanceMap[trackingId] = instance;

        this._decorate();
    }

    /** instances implementing CoachingDiffDecorateConnector can call this */
    unRegister(instance: ObservationFormDecorateConnector): void {
        const item = Object.entries(this._trackingIdInstanceMap).find(
            (e) => e[1] === instance,
        );
        if (item) {
            this._run((_, result) => {
                if (_ !== instance) {
                    return;
                }
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                instance.fdcUnDecorate && instance.fdcUnDecorate(result);
            });
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this._trackingIdInstanceMap[item[0]];
        }
    }

    /** instances implementing CoachingDiffDecorateConnector can call this */
    redecorate(instance: ObservationFormDecorateConnector): void {
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            Object.values(this._trackingIdInstanceMap).indexOf(instance) !== -1
        ) {
            this._run((_, result) => {
                if (_ !== instance) {
                    return;
                }
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                instance.fdcUnDecorate && instance.fdcUnDecorate(result);
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                instance.fdcDecorate && instance.fdcDecorate(result);
            });
        }
    }

    /** only the hierarchical parent of all instances that implement CoachingDiffDecorateConnector should call this */
    decorate(result: ObservationValidationResult): void {
        this.unDecorate();

        this._result = result;

        this._decorate();
    }

    /** only the hierarchical parent of all instances that implement CoachingDiffDecorateConnector should call this */
    unDecorate(): void {
        Object.values(this._trackingIdInstanceMap).forEach((instance) => {
            if (!instance) {
                return;
            }
            this._run((_, result) => {
                if (_ !== instance) {
                    return;
                }
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                instance.fdcUnDecorate && instance.fdcUnDecorate(result);
            });
        });

        this._result = null;
    }

    /** only the hierarchical parent of all instances that implement CoachingDiffDecorateConnector should call this.
     * due to odd requirements and existing behaviour, this will only un-decorate previously invalid items that
     * are now valid. and will replace the internal result the delta one */
    decorateDelta(deltaResult: ObservationValidationResult): void {
        this._run((instance, result, trackingId) => {
            const dr = this._find(deltaResult, trackingId);
            if (!dr) {
                return;
            }
            if (!result.valid) {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                instance.fdcDecorateDelta &&
                    instance.fdcDecorateDelta(result, dr);
                result.valid = dr.valid;

                // TODO could be done differently
                // eslint-disable-next-line no-prototype-builtins
                if (result.hasOwnProperty('focusSelected')) {
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    result['focusSelected'] = dr['focusSelected'];
                }
            }
        });
    }

    private _decorate(): void {
        this._run((instance, result) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            instance.fdcDecorate && instance.fdcDecorate(result);
        });
    }

    private _run(
        predicate: (
            instance: ObservationFormDecorateConnector,
            result: ValidationResult,
            trackingId: number,
        ) => void,
    ): void {
        if (!this._result) {
            return;
        }

        let instance =
            this._trackingIdInstanceMap[FOCUS_CONTAINERS_TRACKING_ID];
        if (instance != null) {
            predicate(instance, this._result, FOCUS_CONTAINERS_TRACKING_ID);
        }
        this._result.focusContainers.forEach((fc) => {
            // TODO could be done differently
            // intended for ignoring untouched containers that are from active commitments on unsaved observations
            if (
                !fc.savedObservation &&
                fc.focusSelected &&
                !fc.trackingChanged
            ) {
                return;
            }

            instance = this._trackingIdInstanceMap[fc.trackingId];
            if (instance != null) {
                predicate(instance, fc, fc.trackingId);
            }
            fc.interactions.forEach((ict) => {
                instance = this._trackingIdInstanceMap[ict.trackingId];
                if (instance != null) {
                    predicate(instance, ict, ict.trackingId);
                }
            });
        });
    }

    private _find(
        result: ObservationValidationResult,
        trackingId: number,
    ): ValidationResult {
        if (!result) {
            return null;
        }
        if (trackingId === FOCUS_CONTAINERS_TRACKING_ID) {
            return result;
        }
        for (const fc of result.focusContainers) {
            if (fc.trackingId === trackingId) {
                return fc;
            }
            for (const ict of fc.interactions) {
                if (ict.trackingId === trackingId) {
                    return ict;
                }
            }
        }
    }
}
